import React from 'react';
import { Line } from 'react-chartjs-2';
import { Chart } from 'chart.js';
import ZoomPlugin from 'chartjs-plugin-zoom';
import DataLabelPlugin from 'chartjs-plugin-datalabels';
import { Age, Tool, VitalsEntity } from '../../../../../../store/types/prescription';
import {
    WEIGHT_TO_HEIGHT,
    externalTooltipHandler,
    feetToCentimeter,
    getTwoToFiveWeightToHeightPercentile,
    getTwoToFiveWeightToHeightZScore,
    getWeightAndHeightVitalsFromVisits,
    getZeroToTwoWeightToHeightPercentile,
    getZeroToTwoWeightToHeightZScore,
    graphIndexColor,
    lbsToKgs,
} from './constants/utils';
import { getAgeInYearsWithoutRoundOff } from '../../../../../../store/utils';
import GrowthChartEmpty from '../GrowthChartEmpty';
import { GROWTH_CHART_TYPE } from './constants/growthChartType';
import { roundoffNumberIfDecimal } from '../../../../../../pages/utils';
import moment from 'moment';

Chart.register([
    ZoomPlugin,
    DataLabelPlugin,
    {
        id: 'tickLinePlugin',
        afterDraw: (chart) => {
            const ctx = chart.ctx;
            const xAxis = chart.scales['x'];
            const yAxis = chart.scales['y'];

            // Draw vertical tick lines for x-axis
            xAxis?.ticks?.forEach((value, index) => {
                const x = xAxis.getPixelForTick(index);
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(x, chart.chartArea.bottom);
                ctx.lineTo(x, chart.chartArea.bottom + 10);
                ctx.strokeStyle = '#000000';
                ctx.lineWidth = 1;
                ctx.stroke();
                ctx.restore();
            });

            // Draw horizontal tick lines for y-axis
            yAxis?.ticks?.forEach((value, index) => {
                const y = yAxis.getPixelForTick(index);
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(chart.chartArea.left - 10, y);
                ctx.lineTo(chart.chartArea.left, y);
                ctx.strokeStyle = '#000000';
                ctx.lineWidth = 1;
                ctx.stroke();
                ctx.restore();
            });
        },
    },
]);

const WeightToHeight: React.FC<{
    age?: Age;
    gender?: string;
    height?: VitalsEntity;
    weight?: VitalsEntity;
    growthChartData: GROWTH_CHART_TYPE;
    visits?: (Tool & {
        date: string;
        visitId: string;
        precriptionId?: string;
        flavour?: string;
        translateLang?: string;
    })[];
}> = ({ age, gender, weight, height, growthChartData, visits }) => {
    const buildOptions = (type: '0-2' | '2-5', isPrintMode?: boolean) => {
        const x: { [key: string]: any } = {
            type: 'linear',
            distribution: 'linear',
            grid: {
                color: '#f7f5f5',
            },
        };

        const y: { [key: string]: any } = {
            type: 'linear',
            distribution: 'linear',
            grid: {
                color: '#f7f5f5',
            },
        };

        if (isPrintMode) {
            x.grid = {
                display: false,
                borderColor: '#000000',
            };

            x.ticks = {
                color: '#000000',
                font: {
                    weight: '600',
                    size: 24,
                },
            };

            y.grid = {
                display: false,
                borderColor: '#000000',
            };

            y.ticks = {
                color: '#000000',
                font: {
                    weight: '600',
                    size: 24,
                },
            };
        }

        if (type === '0-2') {
            x.min = 45;
            x.max = 110;
            x.ticks = {
                ...x.ticks,
                stepSize: 5,
            };
            x.title = {
                text: 'HEIGHT (IN CMS)',
                color: isPrintMode ? '#000000' : '#484848',
                font: {
                    size: isPrintMode ? 24 : 10,
                    weight: '700',
                },
                display: true,
            };
            y.title = {
                text: 'WEIGHT (IN KGS)',
                color: isPrintMode ? '#000000' : '#484848',
                font: {
                    size: isPrintMode ? 24 : 10,
                    weight: '700',
                },
                display: true,
            };
        }

        if (type === '2-5') {
            x.min = 65;
            x.max = 120;
            x.ticks = {
                ...x.ticks,
                stepSize: 5,
            };
            x.title = {
                text: 'HEIGHT (IN CMS)',
                color: isPrintMode ? '#000000' : '#484848',
                font: {
                    size: isPrintMode ? 24 : 10,
                    weight: '700',
                },
                display: true,
            };
            y.title = {
                text: 'WEIGHT (IN KGS)',
                color: isPrintMode ? '#000000' : '#484848',
                font: {
                    size: isPrintMode ? 24 : 10,
                    weight: '700',
                },
                display: true,
            };
        }

        return {
            scales: {
                x: x,
                y: y,
            },
            plugins: {
                zoom: {
                    zoom: {
                        drag: {
                            enabled: true,
                        },
                        mode: 'xy',
                    },
                },
                datalabels: {
                    color: (context: any) => {
                        return context?.datasetIndex === context?.chart?._metasets?.length - 1
                            ? 'black'
                            : isPrintMode
                            ? '#000000'
                            : '#484848';
                    },
                    anchor: 'end',
                    align: (context: any) => {
                        return context?.datasetIndex === context?.chart?._metasets?.length - 1
                            ? 'end'
                            : 'center';
                    },
                    font: (context: any) => {
                        if (context?.datasetIndex === context?.chart?._metasets?.length - 1) {
                            return {
                                size: isPrintMode ? 16 : 12,
                                weight: 'bold',
                                color: 'red',
                            };
                        } else {
                            return {
                                size: isPrintMode ? 16 : 10,
                                weight: '500',
                            };
                        }
                    },
                    formatter: function (value: any, context: any) {
                        if (
                            context?.datasetIndex === context?.chart?._metasets?.length - 1 &&
                            context.dataIndex === context.dataset.data.length - 1
                        ) {
                            return `${context.dataset.label?.[context.dataIndex]}`;
                        } else if (context.dataIndex === context.dataset.data.length - 1) {
                            return `         ${context.dataset.label}`;
                        } else {
                            return '';
                        }
                    },
                },
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                    position: 'nearest',
                    external: (context: any) => {
                        const c = context?.tooltip?.dataPoints?.[0];

                        if (c?.datasetIndex === c?.chart?._metasets?.length - 1) {
                            const data = c?.dataset?.data?.[c?.dataIndex];
                            return externalTooltipHandler(
                                context,
                                'HEIGHT',
                                data?.displayHeight,
                                'WEIGHT',
                                data?.displayWeight,
                                data?.percentile,
                                data?.zScore,
                                data?.visitDate,
                            );
                        }
                    },
                },
            },
            hover: {
                mode: 'index',
            },
            aspectRatio: 1.5,
            animation: {
                duration: 0,
            },
            layout: {
                padding: {
                    top: 32,
                    right: isPrintMode ? 44 : 28,
                    bottom: 16,
                    left: 8,
                },
            },
            elements: {
                line: {
                    fill: false,
                    tension: 0,
                },
            },
        };
    };

    const buildDataOptionsAndError = (isPrintMode?: boolean) => {
        let data = {};
        let options = {};
        let isError = undefined;

        const heightInCms = Number(
            height?.value.code_id === 'vu-3019850218'
                ? feetToCentimeter(parseFloat(height.value.qt))
                : height?.value?.qt,
        );

        const weightInKgs = Number(
            weight?.value.code_id === 'vu-2307875107'
                ? lbsToKgs(parseFloat(weight.value.qt))
                : weight?.value?.qt,
        );

        const ageInNumber = getAgeInYearsWithoutRoundOff(age);

        if (ageInNumber <= 2) {
            let zeroToTwoData = undefined;
            let zScore = undefined;
            let percentile = undefined;

            if (gender?.toLowerCase() === 'male') {
                zeroToTwoData = growthChartData?.zero_to_two_weight_to_height_contants?.male;

                zScore = getZeroToTwoWeightToHeightZScore(
                    growthChartData,
                    'male',
                    heightInCms,
                    weightInKgs,
                );
                percentile = getZeroToTwoWeightToHeightPercentile(
                    growthChartData,
                    'male',
                    heightInCms,
                    weightInKgs,
                );
            }

            if (gender?.toLowerCase() === 'female') {
                zeroToTwoData = growthChartData?.zero_to_two_weight_to_height_contants?.female;

                zScore = getZeroToTwoWeightToHeightZScore(
                    growthChartData,
                    'female',
                    heightInCms,
                    weightInKgs,
                );
                percentile = getZeroToTwoWeightToHeightPercentile(
                    growthChartData,
                    'female',
                    heightInCms,
                    weightInKgs,
                );
            }

            if (isNaN(Number(zScore))) {
                isError =
                    'The Height must be greater than 45 cms, and a weight measurement must be provided in order to calculate percentile and score';
            } else {
                isError = null;
            }

            const lineData = getWeightAndHeightVitalsFromVisits('0-5', visits);

            data = {
                labels: zeroToTwoData?.labels,
                datasets: [
                    {
                        label: '3rd',
                        data: zeroToTwoData?.p_3,
                        backgroundColor: isPrintMode ? '#000000' : '#D74B41',
                        borderColor: isPrintMode ? '#000000' : 'rgba(215, 75, 65, 0.3)',
                        fill: false,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        borderWidth: 1,
                        order: 2,
                    },
                    isPrintMode
                        ? undefined
                        : {
                              label: '15th',
                              data: zeroToTwoData?.p_15,
                              backgroundColor: isPrintMode ? '#000000' : '#484848',
                              borderColor: isPrintMode ? '#000000' : 'rgba(72, 72, 72, 0.3)',
                              fill: false,
                              pointRadius: 0,
                              pointHoverRadius: 0,
                              borderWidth: 1,
                              order: 2,
                          },
                    {
                        label: '25th',
                        data: zeroToTwoData?.p_25,
                        backgroundColor: isPrintMode ? '#000000' : '#484848',
                        borderColor: isPrintMode ? '#000000' : 'rgba(72, 72, 72, 0.3)',
                        fill: false,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        borderWidth: 1,
                        order: 2,
                    },
                    {
                        label: '50th',
                        data: zeroToTwoData?.p_50,
                        backgroundColor: isPrintMode ? '#000000' : '#484848',
                        borderColor: isPrintMode ? '#000000' : 'rgba(72, 72, 72, 0.3)',
                        fill: false,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        borderWidth: 1,
                        order: 2,
                    },
                    {
                        label: '75th',
                        data: zeroToTwoData?.p_75,
                        backgroundColor: isPrintMode ? '#000000' : '#484848',
                        borderColor: isPrintMode ? '#000000' : 'rgba(72, 72, 72, 0.3)',
                        fill: false,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        borderWidth: 1,
                        order: 2,
                    },
                    isPrintMode
                        ? undefined
                        : {
                              label: '85th',
                              data: zeroToTwoData?.p_85,
                              backgroundColor: isPrintMode ? '#000000' : '#484848',
                              borderColor: isPrintMode ? '#000000' : 'rgba(72, 72, 72, 0.3)',
                              fill: false,
                              pointRadius: 0,
                              pointHoverRadius: 0,
                              borderWidth: 1,
                              order: 2,
                          },
                    {
                        label: '97th',
                        data: zeroToTwoData?.p_97,
                        backgroundColor: isPrintMode ? '#000000' : '#19A66A',
                        borderColor: isPrintMode ? '#000000' : 'rgba(25, 166, 106, 0.3)',
                        fill: false,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        borderWidth: 1,
                        order: 2,
                    },
                    {
                        label: [
                            ...(lineData?.map((d) =>
                                d?.percentile === undefined ? '' : `${d?.percentile} %ile`,
                            ) || []),
                            percentile === undefined ? '' : `${percentile} %ile`,
                        ],
                        data: [
                            ...(lineData?.map((d) => ({
                                x: d?.height,
                                y: d?.weight,
                                zScore: d?.zIndex,
                                percentile: d?.percentile,
                                visitDate: moment(d?.visitDate).format('MMMM DD, YYYY'),
                                displayHeight: d?.displayHeight,
                                displayWeight: d?.displayWeight,
                            })) || []),
                            {
                                x: heightInCms,
                                y: weightInKgs,
                                zScore: zScore,
                                percentile: percentile,
                                visitDate: 'Today',
                                displayHeight: `${roundoffNumberIfDecimal(heightInCms)} cms`,
                                displayWeight: `${roundoffNumberIfDecimal(weightInKgs)} kgs`,
                            },
                        ],
                        pointBackgroundColor:
                            [...new Array((lineData?.length || 0) + 1)]?.map((d, i) =>
                                graphIndexColor((lineData?.length || 0) === i ? 1 : 0, isPrintMode),
                            ) || [],
                        pointBorderColor:
                            [...new Array((lineData?.length || 0) + 1)]?.map((d, i) =>
                                graphIndexColor((lineData?.length || 0) === i ? 1 : 0, isPrintMode),
                            ) || [],
                        borderColor: isPrintMode ? '#000000' : '#215FFF',
                        pointRadius: 5,
                        pointHoverRadius: 7,
                        pointStyle: 'circle',
                        borderWidth: 1.5,
                        order: 1,
                    },
                ].filter(Boolean),
            };

            options = buildOptions('0-2', isPrintMode);
        } else if (2 < ageInNumber && ageInNumber <= 5) {
            let twoToFiveData = undefined;
            let zScore = undefined;
            let percentile = undefined;

            if (gender?.toLowerCase() === 'male') {
                twoToFiveData = growthChartData?.two_to_five_weight_to_height_contants?.male;

                zScore = getTwoToFiveWeightToHeightZScore(
                    growthChartData,
                    'male',
                    heightInCms,
                    weightInKgs,
                );
                percentile = getTwoToFiveWeightToHeightPercentile(
                    growthChartData,
                    'male',
                    heightInCms,
                    weightInKgs,
                );
            }

            if (gender?.toLowerCase() === 'female') {
                twoToFiveData = growthChartData?.two_to_five_weight_to_height_contants?.female;

                zScore = getTwoToFiveWeightToHeightZScore(
                    growthChartData,
                    'female',
                    heightInCms,
                    weightInKgs,
                );
                percentile = getTwoToFiveWeightToHeightPercentile(
                    growthChartData,
                    'female',
                    heightInCms,
                    weightInKgs,
                );
            }

            if (isNaN(Number(zScore))) {
                isError =
                    'The Height must be greater than 65 cms, and a weight measurement must be provided in order to calculate percentile and score';
            } else {
                isError = null;
            }

            const lineData = getWeightAndHeightVitalsFromVisits('0-5', visits);

            data = {
                labels: twoToFiveData?.labels,
                datasets: [
                    {
                        label: '3rd',
                        data: twoToFiveData?.p_3,
                        backgroundColor: isPrintMode ? '#000000' : '#D74B41',
                        borderColor: isPrintMode ? '#000000' : 'rgba(215, 75, 65, 0.3)',
                        fill: false,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        borderWidth: 1,
                        order: 2,
                    },
                    isPrintMode
                        ? undefined
                        : {
                              label: '15th',
                              data: twoToFiveData?.p_15,
                              backgroundColor: isPrintMode ? '#000000' : '#484848',
                              borderColor: isPrintMode ? '#000000' : 'rgba(72, 72, 72, 0.3)',
                              fill: false,
                              pointRadius: 0,
                              pointHoverRadius: 0,
                              borderWidth: 1,
                              order: 2,
                          },
                    {
                        label: '25th',
                        data: twoToFiveData?.p_25,
                        backgroundColor: isPrintMode ? '#000000' : '#484848',
                        borderColor: isPrintMode ? '#000000' : 'rgba(72, 72, 72, 0.3)',
                        fill: false,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        borderWidth: 1,
                        order: 2,
                    },
                    {
                        label: '50th',
                        data: twoToFiveData?.p_50,
                        backgroundColor: isPrintMode ? '#000000' : '#484848',
                        borderColor: isPrintMode ? '#000000' : 'rgba(72, 72, 72, 0.3)',
                        fill: false,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        borderWidth: 1,
                        order: 2,
                    },
                    {
                        label: '75th',
                        data: twoToFiveData?.p_75,
                        backgroundColor: isPrintMode ? '#000000' : '#484848',
                        borderColor: isPrintMode ? '#000000' : 'rgba(72, 72, 72, 0.3)',
                        fill: false,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        borderWidth: 1,
                        order: 2,
                    },
                    isPrintMode
                        ? undefined
                        : {
                              label: '85th',
                              data: twoToFiveData?.p_85,
                              backgroundColor: isPrintMode ? '#000000' : '#484848',
                              borderColor: isPrintMode ? '#000000' : 'rgba(72, 72, 72, 0.3)',
                              fill: false,
                              pointRadius: 0,
                              pointHoverRadius: 0,
                              borderWidth: 1,
                              order: 2,
                          },
                    {
                        label: '97th',
                        data: twoToFiveData?.p_97,
                        backgroundColor: '#19A66A',
                        borderColor: isPrintMode ? '#000000' : 'rgba(25, 166, 106, 0.3)',
                        fill: false,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        borderWidth: 1,
                        order: 2,
                    },
                    {
                        label: [
                            ...(lineData?.map((d) =>
                                d?.percentile === undefined ? '' : `${d?.percentile} %ile`,
                            ) || []),
                            percentile === undefined ? '' : `${percentile} %ile`,
                        ],
                        data: [
                            ...(lineData?.map((d) => ({
                                x: d?.height,
                                y: d?.weight,
                                zScore: d?.zIndex,
                                percentile: d?.percentile,
                                visitDate: moment(d?.visitDate).format('MMMM DD, YYYY'),
                                displayHeight: d?.displayHeight,
                                displayWeight: d?.displayWeight,
                            })) || []),
                            {
                                x: heightInCms,
                                y: weightInKgs,
                                zScore: zScore,
                                percentile: percentile,
                                visitDate: 'Today',
                                displayHeight: `${roundoffNumberIfDecimal(heightInCms)} cms`,
                                displayWeight: `${roundoffNumberIfDecimal(weightInKgs)} kgs`,
                            },
                        ],
                        pointBackgroundColor:
                            [...new Array((lineData?.length || 0) + 1)]?.map((d, i) =>
                                graphIndexColor((lineData?.length || 0) === i ? 1 : 0, isPrintMode),
                            ) || [],
                        pointBorderColor:
                            [...new Array((lineData?.length || 0) + 1)]?.map((d, i) =>
                                graphIndexColor((lineData?.length || 0) === i ? 1 : 0, isPrintMode),
                            ) || [],
                        borderColor: isPrintMode ? '#000000' : '#215FFF',
                        pointRadius: 5,
                        pointHoverRadius: 7,
                        pointStyle: 'circle',
                        borderWidth: 1.5,
                        order: 1,
                    },
                ].filter(Boolean),
            };

            options = buildOptions('2-5', isPrintMode);
        }

        return { data, options, isError };
    };

    const { data, options, isError } = buildDataOptionsAndError();
    const { data: printData, options: printOptions } = buildDataOptionsAndError(true);

    return (
        <div>
            <span className="text-16 font-700 ml-16">Weight For Height</span>
            {isError && (
                <div className="px-16">
                    <GrowthChartEmpty text={isError} />
                </div>
            )}
            <div>
                <Line data={data} options={options} plugins={[ZoomPlugin, DataLabelPlugin]} />
            </div>
            <div style={{ width: '210mm', height: '0px', overflowY: 'scroll' }}>
                <Line
                    id={WEIGHT_TO_HEIGHT}
                    data={printData}
                    options={printOptions}
                    plugins={[ZoomPlugin, DataLabelPlugin]}
                />
            </div>
        </div>
    );
};

export default WeightToHeight;
